import type { FirebaseApp } from 'firebase/app';
import type { PerformanceTrace } from 'firebase/performance';

export type PerformanceTraceName =
  | 'app_ready'
  | 'routine_ready'
  | 'trainer_dashboard_ready';

export type ActivePerformanceTrace = {
  stop: () => void;
};

type FirebasePerformanceModule = typeof import('firebase/performance');
type PerformanceContext = {
  performance: ReturnType<FirebasePerformanceModule['getPerformance']>;
  trace: FirebasePerformanceModule['trace'];
};

let performancePromise: Promise<PerformanceContext | null> | null = null;

const privateEntryPathPrefixes = [
  '/app/invite/',
  '/app/routine-preview/',
  '/app/watch/claim',
  '/invite/',
  '/routine-preview/',
  '/trainer/clients/',
  '/trainer/routines/',
  '/watch/claim',
];

/**
 * Page-load traces include the page URL. Do not initialise the SDK on entry
 * routes whose path can carry an invite, pairing, client, or routine id.
 */
export function shouldCollectPerformanceForPath(pathname: string) {
  return !privateEntryPathPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix),
  );
}

export function initFirebasePerformance(
  app: FirebaseApp | null,
  pathname = typeof window === 'undefined' ? '' : window.location.pathname,
) {
  const enabled =
    import.meta.env.PROD &&
    import.meta.env.VITE_FIREBASE_USE_EMULATORS !== 'true' &&
    import.meta.env.VITE_FIREBASE_PERFORMANCE_ENABLED !== 'false';

  if (
    !enabled ||
    !app ||
    performancePromise ||
    !shouldCollectPerformanceForPath(pathname)
  ) {
    return;
  }

  performancePromise = import('firebase/performance')
    .then(({ getPerformance, trace }) => ({
      performance: getPerformance(app),
      trace,
    }))
    .catch((error) => {
      console.warn('Firebase Performance Monitoring is unavailable.', error);
      return null;
    });
}

export function startFirebasePerformanceTrace(
  name: PerformanceTraceName,
): ActivePerformanceTrace {
  let activeTrace: PerformanceTrace | null = null;
  let stopped = false;

  void performancePromise?.then((context) => {
    if (!context || stopped) {
      return;
    }

    activeTrace = context.trace(context.performance, name);
    activeTrace.start();
  });

  return {
    stop() {
      if (stopped) {
        return;
      }

      stopped = true;
      activeTrace?.stop();
      activeTrace = null;
    },
  };
}
